import {
  AddConversationAttachment,
  ViewConversationAttachment,
  ViewConversationEntry
} from '../../api/dealer-api-interface-franchisee';

import { asMarkdownTemplateResult } from '../../../webmodule-common/other/general/markdown';
// autoElement no longer used here; switched to explicit @customElement tags
import { buildModifiedTime } from './conversation-helper';
import { cache } from '../cache-impl/cache-registry';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { html, TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';

import { awaitableTemplate } from '../../../webmodule-common/other/ui/template-processor';
import { LangDealerFranchiseeConversationWmConversationEntry } from '../../../localization/scripts/dealer-franchisee/conversation/wm-conversation-entry-lang';
import { lapsedMinutes } from '../../../webmodule-common/other/general/time';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { previewImage } from '../../../webmodule-common/other/ui/modal-img';
import { repeat } from 'lit/directives/repeat.js';
import {
  serverDateTimeToLocal,
  serverDateTimeToLocalDateTime
} from '../../../webmodule-common/other/datetime-converter';

// Legacy guard removed; direct event dispatch.

@customElement('wmview-conversation-users')
export class WebModuleConversationUsersView extends LitElementBase {
  async updateUserNames() {
    const names = await cache().userProfile.getMany(this.users);
    this.userNames = names?.map(x => `${x.displayValue}`) ?? [];
  }

  @property({ type: Array, attribute: false })
  private _users: string[] = [];
  public get users(): string[] {
    return this._users;
  }

  public set users(value: string[]) {
    this._users = value;
    this.updateUserNames();
  }

  @state() userNames: string[] = [];

  render() {
    const userNameTemplates = this.userNames.map(x => this.userNameTemplate(x));
    if (this.userNames.length === 0) return html``;
    return html` <div class="conversation-names">
      <label>${LangDealerFranchiseeConversationWmConversationEntry.attention()}</label>${userNameTemplates}
    </div>`;
  }

  userNameTemplate(userDisplayName: string): any {
    return html` <webmodule-tag pill variant="neutral" size="small">${userDisplayName}</webmodule-tag>`;
  }
}

// Legacy update event payload mapped onto unified updateBranchQuoteSupport shape.
export interface ConversationEntryUpdateEvent {
  editor: WMConversationEntryEditorBase;
  input: {
    id: string;
    text: string;
    attachments: AddConversationAttachment[];
    users: string[];
    draft: boolean;
    deleteEntry?: boolean;
    forceUpdate?: boolean;
  };
}

class WMConversationEntryEditorBase extends LitElementBase {
  @property({ type: Boolean }) forceUpdate = false;
  protected tenantId = getCurrentUser()?.tenantId;

  @state()
  lapsedMinutes = 0;

  @property({ type: Boolean }) attachmentsOnly = false;

  protected isUserSameTenant(id: string) {
    return cache().userProfile.getLocalData(id)?.tenantId === this.tenantId;
  }

  @state() lastModified? = '';
  @property({ attribute: false })
  private _conversationEntry?: ViewConversationEntry | undefined;
  public get conversationEntry(): ViewConversationEntry | undefined {
    return this._conversationEntry;
  }

  public set conversationEntry(value: ViewConversationEntry | undefined) {
    //LIT Tracks state by reference, not content.
    //when we reassign the same object back, it doesn't automatically trigger repaints
    //by adding the lastmodified as a stateful, we ensure proper refreshing of content
    this.lastModified = value?.lastModified;

    this._conversationEntry = value;
    if (this._conversationEntry) {
      this.lapsedMinutes = lapsedMinutes(serverDateTimeToLocal(this._conversationEntry.dateCreated));

      this.updateUserName();
      this.dateCreated = serverDateTimeToLocalDateTime(this._conversationEntry.dateCreated).toLocaleString(
        DateTime.DATETIME_FULL
      );
      const modifedDate = serverDateTimeToLocalDateTime(this._conversationEntry.dateCreated);
      this.modifiedTime = buildModifiedTime(modifedDate);
    } else {
      this.userName = '';
      this.lapsedMinutes = 0;
    }
    this.setStates();
  }

  @state() protected userName = '';
  @state() protected dateCreated = '';
  @state() protected modifiedTime = '';
  @property({ type: Number }) maxFiles = 20;
  @property({ type: Boolean }) allowCancel = true;

  async updateUserName() {
    if (this.conversationEntry)
      this.userName = (await cache().userProfile.get(this.conversationEntry?.systemUserId))?.displayValue ?? '';
  }

  eventClickChild = async (e: Event) => {
    if (e.target instanceof HTMLImageElement) {
      e.preventDefault();
      e.stopImmediatePropagation();
      await previewImage(e.target.src);
    }
  };

  get isAuthorSupplier(): boolean {
    return this.conversationEntry !== undefined && !this.isUserSameTenant(this.conversationEntry.systemUserId);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('click', this.eventClickChild);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.eventClickChild);
  }

  get conversationEntryClasses() {
    return this.isAuthorSupplier ? 'conversation-entry author-supplier' : 'conversation-entry author-dealer';
  }

  get conversationIconTemplate() {
    return !this.isAuthorSupplier
      ? html`<i class="fa-regular fa-comment conversation-icon"></i>`
      : html`<i class="fa-solid fa-comments conversation-icon conversation-supplier-icon"></i>`;
  }

  get isOwner() {
    return getCurrentUser()?.id == this.conversationEntry?.systemUserId;
  }

  @state() isDraft = false;
  @state() isEditable = false;

  setStates() {
    this.isDraft = this.isOwner && (this.conversationEntry?.draft ?? false);
    if (!this.conversationEntry) return;
    const expired = this.lapsedMinutes > 25;
    if (!expired) console.log(`lapsed time on ${this.lapsedMinutes}`);
    this.isEditable = !this.isReadonly && this.isOwner && (!expired || this.isDraft);
  }

  get isReadonly() {
    return this.readonly || this.attributeTrue('readonly');
  }

  protected get embeddedAttachmentsAsInputs() {
    return (
      this.conversationEntry?.attachments
        .filter(x => x.embedded)
        .map(x => {
          return { filename: x.filename };
        }) ?? []
    );
  }

  protected get embeddedAttachments() {
    return this.conversationEntry?.attachments.filter(x => x.embedded) ?? [];
  }

  renderHeader() {
    return html` <div>
      <span class="user-name fw-bold">${this.userName}</span>

      <span class="date-created">${this.dateCreated}</span>

      <span class="last-edit"
        >${LangDealerFranchiseeConversationWmConversationEntry.posted()} ${this.modifiedTime}</span
      >
    </div>`;
  }

  renderView() {
    const rawText = this.conversationEntry?.text ?? '';

    if (isEmptyOrSpace(rawText))
      return html`<span class="text-muted"
        >${LangDealerFranchiseeConversationWmConversationEntry.noContentInEntry()}</span
      >`;

    return awaitableTemplate(this.renderProcessedConversationText(rawText));
  }

  private async renderProcessedConversationText(rawText: string): Promise<TemplateResult> {
    const processedText = await this.preprocessConversationText(rawText);

    return isEmptyOrSpace(processedText)
      ? html`<span class="text-muted">${LangDealerFranchiseeConversationWmConversationEntry.noContentInEntry()}</span>`
      : asMarkdownTemplateResult(processedText);
  }

  protected async preprocessConversationText(text: string): Promise<string> {
    if (!text) return '';

    const markerExpression = /<!UserId:\s*(.*?)\)!>/gi;
    const matches = [...text.matchAll(markerExpression)];
    if (matches.length === 0) return text;

    const userIds = Array.from(
      new Set(
        matches.map(match => match[1]?.replace(/[{}]/g, '').trim()).filter((value): value is string => Boolean(value))
      )
    );

    const resolvedUsers = await Promise.all(
      userIds.map(async userId => {
        const userRecord = await cache().userProfile.get(userId);
        return [userId, userRecord?.displayValue ?? null] as const;
      })
    );

    const displayMap = new Map(resolvedUsers.filter(([, display]) => !!display) as [string, string][]);

    return text.replace(markerExpression, (match, capturedValue) => {
      const userId = capturedValue?.replace(/[{}]/g, '').trim();
      if (!userId) return match;

      const displayName = displayMap.get(userId);
      return displayName ? `**${displayName}**` : match;
    });
  }

  defaultHeaderTemplate() {
    return this.attachmentsOnly
      ? html` <div class="conversation-entry-header fw-bold">${this.renderHeader()}</div>`
      : html` <div class="conversation-entry-header fw-bold">
          ${this.renderHeader()}
          <wmview-conversation-users .users=${this.conversationEntry?.users ?? []}></wmview-conversation-users>
        </div>`;
  }

  protected getHeaderTemplate(): TemplateResult {
    return this.defaultHeaderTemplate();
  }

  protected attachmentsTemplate(): TemplateResult {
    return html``;
  }

  protected footerTemplate(): TemplateResult {
    return html``;
  }

  render() {
    const conversationIcon = this.conversationIconTemplate;
    const content = this.renderView();
    const headerTemplate = this.getHeaderTemplate();
    const classes = this.conversationEntryClasses;

    return this.attachmentsOnly
      ? html` <div class=${classes}>${conversationIcon} ${headerTemplate} ${this.attachmentsTemplate()}</div>`
      : html`
          <div class=${classes}>
            ${conversationIcon} ${headerTemplate}
            <div class="conversation-text">${content}</div>
            ${this.attachmentsTemplate()} ${this.footerTemplate()}
          </div>
        `;
  }

  protected readonlyAttachmentsTemplate() {
    const attachmentTemplate = (item: ViewConversationAttachment) => {
      return html`
        <div class="conversation-attachment d-inline me-3">
          <i class="fa-regular fa-file-lines me-1"></i><a target="_blank" href=${item.publicUrl}>${item.filename}</a>
        </div>
      `;
    };
    const attachmentTemplate1 = (item: ViewConversationAttachment) => {
      return html`
        <div class="col-12 conversation-attachment">
          <i class="fa-regular fa-file-lines me-1"></i><a target="_blank" href=${item.publicUrl}>${item.filename}</a>
        </div>
      `;
    };

    if (!this.conversationEntry) return html``;
    const attachments = this.conversationEntry.attachments.filter(x => !x.embedded);
    //not editing

    if (attachments.length === 0) return html``;

    return this.attachmentsOnly
      ? html` <div class="conversation-attachments-section mt-2">
          ${repeat(
            attachments,
            item => item.virtualPath,
            item => attachmentTemplate1(item)
          )}
        </div>`
      : html` <div class="conversation-attachments-section mt-2">
          <div>${LangDealerFranchiseeConversationWmConversationEntry.attachments()}</div>
          ${repeat(
            attachments,
            item => item.virtualPath,
            item => attachmentTemplate(item)
          )}
        </div>`;
  }
}

@customElement('wmview-conversation-entry-view')
export class WebModuleConversationEntryView extends WMConversationEntryEditorBase {
  protected eventEdit = () => {
    // External editing path – emit event so parent conversation component can surface a standalone editor.
    // Added debug log to aid in diagnosing missing edit activation reports.
    // (We relax the 25 minute expiry rule for external editing in setStates override below.)
    // eslint-disable-next-line no-console
    console.info('[conversation-entry] dispatching request-edit for entry', this.conversationEntry?.id);
    this.dispatchCustom('request-edit', {
      editor: this,
      entry: this.conversationEntry
    });
  };

  protected attachmentsTemplate(): TemplateResult {
    return this.readonlyAttachmentsTemplate();
  }
}
